// Internal "at-risk brief" derivation (internal at-risk brief §1/§2).
//
// A brief is heading for too few quotes while there's still time to act. All of
// this is DERIVED from status + deadline + response counts, never stored — the
// same rule as the overdue modifier. Both thresholds are configurable.
//
// This is the internal counterpart to the supplier's 48h deadline urgency
// (lib/deadlineUrgency): the supplier window is deliberately tight (don't alarm
// early); the internal window is wider so Fusion can chase before it's too late
// for the supplier to respond at all.

export const AT_RISK_DEFAULTS = {
  // Wider than the supplier's 48h — chasing at 47 hours leaves no time for them
  // to respond.
  windowDays: 5,
  // Fewer than this many submitted quotes can't be compared, so the brief is at
  // risk of an unusable response set.
  sufficientQuotes: 2,
};

// Supplier engagement states, ranked by chase value (§2). The point of the
// feature: someone with a half-finished quote is far likelier to convert than
// someone who never engaged, so a chase list ranks by this.
//
// NB there is no reliable "opened / viewed" signal in the data
// (last_seen_version bumps on submit, not on view), so a 'pending' recipient is
// "not started" — the best available proxy for "not opened". We never claim a
// brief was viewed when we can't know it.
export const ENGAGEMENT = {
  started:    { key: 'started',     label: 'Started, not submitted', chaseRank: 4 },
  accepted:   { key: 'accepted',    label: 'Accepted, not started',  chaseRank: 3 },
  notStarted: { key: 'not_started', label: 'Not started',            chaseRank: 2 },
  submitted:  { key: 'submitted',   label: 'Submitted',              chaseRank: 1 },
  declined:   { key: 'declined',    label: 'Declined',               chaseRank: 0 },
};

// Engagement state for one recipient. `hasDraft` = a non-submitted brief_response
// exists (resolved by the caller from EXISTENCE only — never draft content, §7).
export function engagementState(recipient, { hasDraft = false } = {}) {
  const s = recipient?.status;
  if (s === 'responded') return ENGAGEMENT.submitted;
  if (s === 'declined') return ENGAGEMENT.declined;
  if (hasDraft) return ENGAGEMENT.started;       // draft beats 'accepted' — real effort
  if (s === 'accepted') return ENGAGEMENT.accepted;
  return ENGAGEMENT.notStarted;                  // 'pending' / anything else
}

// Engagement mix across a brief's recipients — the segmented-bar + summary
// source. Each recipient carries a resolved `hasDraft` flag.
export function engagementMix(recipients) {
  const mix = { submitted: 0, started: 0, accepted: 0, notStarted: 0, declined: 0, total: 0 };
  for (const r of recipients || []) {
    const st = engagementState(r, { hasDraft: r.hasDraft });
    mix.total += 1;
    if (st === ENGAGEMENT.submitted) mix.submitted += 1;
    else if (st === ENGAGEMENT.started) mix.started += 1;
    else if (st === ENGAGEMENT.accepted) mix.accepted += 1;
    else if (st === ENGAGEMENT.declined) mix.declined += 1;
    else mix.notStarted += 1;
  }
  return mix;
}

// One-line summary of the mix, e.g. "1 of 5 in · 2 started" (§3.3).
export function engagementSummary(mix) {
  if (!mix || mix.total === 0) return '';
  const parts = [`${mix.submitted} of ${mix.total} in`];
  if (mix.started > 0) parts.push(`${mix.started} started`);
  return parts.join(' · ');
}

// Is this brief at-risk? Live 'sent' brief, deadline still ahead but inside the
// window, and too few submitted quotes. Exclusive briefs go to a single
// supplier, so "2 quotes" is impossible — an exclusive brief is at risk when
// its one supplier hasn't submitted (§1).
export function isBriefAtRisk(brief, { submittedCount = 0, now, config = AT_RISK_DEFAULTS } = {}) {
  if (brief?.status !== 'sent') return false;   // not draft/awarded/closed/cancelled
  const iso = brief?.required_response_time;
  if (!iso) return false;
  const ms = new Date(iso).getTime() - (now || Date.now());
  if (ms < 0) return false;                      // passed → overdue, a separate state
  if (ms > config.windowDays * 86_400_000) return false;  // outside the window
  if (brief?.brief_type === 'exclusive') return submittedCount < 1;
  return submittedCount < config.sufficientQuotes;
}
